import { Button, Modal, Popover, Table } from "antd";
import { useParams } from "react-router-dom";
import {
    ApiOutlined,
    DownloadOutlined,
    LinkOutlined,
    PlusOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useRef, useState } from "react";
import Toolbar from "../common/Toolbar";
import axiosInstance from "../api";
import { downloadFile, withTooltip } from "../util";
import NewSectionItem from "./NewSectionItem";
import EditSectionItem from "./EditSectionItem";
import ImportOrderAccounting from "./ImportOrderAccounting";
import { AccountingContext } from "./AccountingDetails";
import dayjs from "dayjs";
import NewLinkedSupplyItem from "./NewLinkedSupplyItem";
import CornerSvg from "../corner.svg";

const columns = [
    {
        key: "section",
        dataIndex: ["section", "name"],
        title: "Tratta",
        width: 150,
        fixed: true,
        ellipsis: true,
    },
    {
        key: "refId",
        dataIndex: "contractItem",
        title: "Voce",
        width: 120,
        fixed: true,
        ellipsis: true,
        render: (le, record) =>
            withTooltip(
                <>
                    {record.supplyOfId && (
                        <span style={{ marginRight: 15 }}>
                            <img width={12} src={CornerSvg} />
                        </span>
                    )}

                    {le?.refId ?? "EXTRA"}
                </>,
            ),
    },
    {
        key: "description",
        dataIndex: "listingElement",
        title: "Descrizione",
        width: 250,
        ellipsis: true,
        render: (le, record) => withTooltip(le?.description ?? record.description),
    },
    {
        key: "quantity",
        dataIndex: "quantity",
        align: "end",
        width: 70,
        ellipsis: true,
        title: "Quantità",
    },
    {
        key: "notes",
        dataIndex: "notes",
        title: "Note",
        width: 170,
        render: (x) => withTooltip(x),
        ellipsis: true,
    },
    {
        key: "team",
        dataIndex: "team",
        title: "Squadra",
        width: 150,
        render: (x) => withTooltip(x?.name),
        ellipsis: true,
    },

    /*{
        key: 'createdBy',
        dataIndex: 'createdBy',
        title: 'Creato da',
        width: 150,
        ellipsis: true,
        render: x => x.firstName + " " + x.lastName
    },
    {
        key: 'creationDate',
        dataIndex: 'creationDate',
        title: 'Data creazione',
        width: 150,
        ellipsis: true,
        render: formatDateTime
    },
    {
        key: 'updatedBy',
        dataIndex: 'updatedBy',
        title: 'Modificato da',
        width: 150,
        ellipsis: true,
        render: x => x.firstName + " " + x.lastName
    },
    {
        key: 'updateDate',
        dataIndex: 'updateDate',
        title: 'Data modifica',
        width: 150,
        ellipsis: true,
        render: formatDateTime
    },*/
];

export default function OrderAccountingItems({ style }) {
    const { accountingId } = useParams();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editItemOpen, setEditItemOpen] = useState(false);
    const [newItemOpen, setNewItemOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [scroll, setScroll] = useState(false);
    const [data, setData] = useState(false);
    const accounting = useContext(AccountingContext);
    const locked = accounting?.lockDate && dayjs().isAfter(dayjs(accounting.lockDate));
    const [newLinkedSupplyOpen, setNewLinkedSupplyItemOpen] = useState(false);

    const tableRef = useRef(null);

    const deleteSelectedRecords = (keys) => {
        setLoading(true);
        axiosInstance
            .delete(`/accountingItem?${keys.map((x) => `ids=${x}`).join("&")}`)
            .then(() => setSelectedRowKeys([]))
            .then(() => fetch())
            .finally(() => setLoading(false));
    };

    const fetch = () => {
        setLoading(true);
        axiosInstance
            .get("accountingItem", { params: { accountingId, pageSize: 99999, page: 0 } })
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetch();
    }, []);

    useEffect(() => {
        const tableItems = !!data
            ? data
                  .map((x) => ({ ...x, key: x.id }))
                  .sort((a, b) => {
                      const APrimaryId = a.supplyOfId ?? a.id;
                      const BPrimaryId = b.supplyOfId ?? b.id;

                      const ASecondaryId = !!a.supplyOfId ? a.id : -1;
                      const BSecondaryId = !!b.supplyOfId ? b.id : -1;
                      const diff = APrimaryId - BPrimaryId;
                      return diff === 0 ? ASecondaryId - BSecondaryId : diff;
                  })
            : [];
        const scroll = tableItems.length > items.length;
        setItems(tableItems);
        setScroll(scroll);
    }, [data]);

    useEffect(() => {
        if (scroll) {
            tableRef.current.scrollTo({ index: items.length - 1 });
            setScroll(false);
        }
    }, [scroll]);

    return (
        <div {...{ style }}>
            <EditSectionItem
                open={editItemOpen}
                onFinish={() => {
                    fetch();
                    setEditItemOpen(false);
                }}
                itemId={items.filter((x) => x.id === selectedRowKeys[0])[0]?.id}
                onCancel={() => setEditItemOpen(false)}
            />
            <NewLinkedSupplyItem
                open={newLinkedSupplyOpen}
                onFinish={() => {
                    fetch();
                    setNewLinkedSupplyItemOpen(false);
                    setSelectedRowKeys([]);
                }}
                item={items.filter((x) => x.id === selectedRowKeys[0])[0]}
                onCancel={() => setNewLinkedSupplyItemOpen(false)}
            />
            <NewSectionItem
                open={newItemOpen}
                onOpenChange={setNewItemOpen}
                onOk={() => {
                    fetch();
                    setNewItemOpen(false);
                }}
            />

            <Toolbar
                topDivider={false}
                selectedKeys={selectedRowKeys}
                addAction={() => {}}
                addButton={
                    <Button
                        type={"primary"}
                        icon={<PlusOutlined />}
                        disabled={locked}
                        onClick={() => {
                            setNewItemOpen(true);
                        }}
                    >
                        Nuova riga
                    </Button>
                }
                editAction={(key) => {
                    setEditItemOpen(true);
                }}
                deleteAction={deleteSelectedRecords}
                extra={(keys) => {
                    const item = items.filter((x) => x.id === keys[0])[0];
                    return (
                        <Button
                            type="text"
                            disabled={keys.length !== 1 || !item}
                            icon={<ApiOutlined />}
                            onClick={() => setNewLinkedSupplyItemOpen(true)}
                        >
                            Aggiungi fornitura
                        </Button>
                    );
                }}
            />
            <Table
                loading={loading}
                size={"small"}
                scroll={{ x: columns.reduce((acc, x) => x.width + acc, 0), y: 500 }}
                tableLayout={"fixed"}
                columns={columns}
                ref={tableRef}
                virtual
                pagination={false}
                rowClassName={(record, index) =>
                    record.id === selectedRowKeys[0] ? "scroll-row" : ""
                }
                onRow={(record, rowIndex) => {
                    return {
                        onClick: () => {
                            !locked && setSelectedRowKeys([record.key]);
                        }, // click row
                    };
                }}
                rowSelection={{
                    selectedRowKeys: selectedRowKeys,
                    onChange: setSelectedRowKeys,
                    columnWidth: 60,
                    getCheckboxProps: () => ({
                        disabled: locked,
                    }),
                }}
                dataSource={items}
            />
        </div>
    );
}
